import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { fetchOrderById } from '../../utils/orderApi';
import { fetchOrderItems } from '../../utils/orderItemApi';
import { formatOrderMoney, formatOrderDate } from './orderUtils';
import '../css/Category.css';
import '../css/cartAdmin.css';

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;

    let active = true;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchOrderById(id),
      fetchOrderItems({ orderId: id, pageIndex: 1, pageSize: 100 }),
    ])
      .then(([orderData, itemResult]) => {
        if (!active) return;
        if (!orderData) {
          setError('Order not found.');
          setOrder(null);
          setItems([]);
          return;
        }
        setOrder(orderData);
        setItems(itemResult.items ?? []);
      })
      .catch((e) => {
        if (!active) return;
        setError(e.message || 'Could not load order.');
        setOrder(null);
        setItems([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [id]);

  const subTotal = order?.SubTotal ?? order?.subTotal ?? 0;
  const discount = order?.DiscountAmount ?? order?.discountAmount ?? 0;
  const shipping = order?.ShippingAmount ?? order?.shippingAmount ?? 0;
  const tax = order?.TaxAmount ?? order?.taxAmount ?? 0;
  const grandTotal = order?.TotalAmount ?? order?.totalAmount ?? 0;
  const totalQty = items.reduce((sum, item) => sum + Number(item.Quantity ?? item.quantity ?? 0), 0);

  return (
    <AdminLayout>
      <div className="cart-detail-page">
        <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
          <AdminLink to="/admin">Dashboard</AdminLink>
          <span style={{ margin: '0 6px' }}>/</span>
          <AdminLink to="/admin/order">Orders</AdminLink>
          <span style={{ margin: '0 6px' }}>/</span>
          <span>Order #{id}</span>
        </p>

        <div className="row">
          <div className="col-lg-12 grid-margin stretch-card">
            <div className="card" style={{ border: '1px solid #e8ecf1', borderRadius: '14px', boxShadow: '0 2px 12px rgba(15,23,42,0.04)' }}>
              <div className="card-body" style={{ padding: '1.75rem' }}>
                <div className="cart-detail-header">
                  <h4 className="card-title">Order Details #{id}</h4>
                  <button type="button" className="cart-detail-back-btn" onClick={() => navigate('/admin/order')}>
                    ← Back to Orders
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger py-2 mb-0" style={{ fontSize: '0.875rem' }}>{error}</div>
                ) : order ? (
                  <>
                    <div className="cart-detail-meta">
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Order #</span>
                        <span className="cart-detail-meta-value">{order.OrderNumber ?? order.orderNumber ?? '—'}</span>
                      </div>
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">User ID</span>
                        <span className="cart-detail-meta-value">{order.UserId ?? order.userId}</span>
                      </div>
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Status</span>
                        <span className="cart-detail-meta-value">
                          <span className="cart-detail-status">{order.OrderStatus ?? order.orderStatus ?? '—'}</span>
                        </span>
                      </div>
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Payment</span>
                        <span className="cart-detail-meta-value">{order.PaymentStatus ?? order.paymentStatus ?? '—'}</span>
                      </div>
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Ordered At</span>
                        <span className="cart-detail-meta-value">{formatOrderDate(order.OrderedAt ?? order.orderedAt)}</span>
                      </div>
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Total Qty</span>
                        <span className="cart-detail-meta-value">{totalQty}</span>
                      </div>
                    </div>

                    {items.length === 0 ? (
                      <p className="cart-detail-empty">No items in this order.</p>
                    ) : (
                      <>
                        <div className="cart-detail-table-wrap">
                          <table className="cart-detail-table">
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>SKU</th>
                                <th className="cart-detail-th-qty">Qty</th>
                                <th className="cart-detail-th-price">Unit Price</th>
                                <th className="cart-detail-th-total">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item) => (
                                <tr key={item.OrderItemId ?? item.orderItemId ?? `${item.ProductId ?? item.productId}-${item.SKU ?? item.sku}`}>
                                  <td>
                                    <span className="cart-detail-product-name">{item.ProductName ?? item.productName ?? '—'}</span>
                                    {(item.ProductId ?? item.productId) != null && (
                                      <span className="cart-detail-product-id">ID: {item.ProductId ?? item.productId}</span>
                                    )}
                                  </td>
                                  <td>{item.SKU ?? item.sku ?? '—'}</td>
                                  <td className="cart-detail-td-qty">{item.Quantity ?? item.quantity}</td>
                                  <td className="cart-detail-td-price">{formatOrderMoney(item.UnitPrice ?? item.unitPrice)}</td>
                                  <td className="cart-detail-td-total">{formatOrderMoney(item.TotalPrice ?? item.totalPrice)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="cart-detail-summary">
                          <div className="cart-detail-summary-box">
                            <div className="cart-detail-summary-row">
                              <span className="cart-detail-summary-label">Subtotal</span>
                              <span className="cart-detail-summary-amount">{formatOrderMoney(subTotal)}</span>
                            </div>
                            {Number(discount) > 0 && (
                              <div className="cart-detail-summary-row">
                                <span className="cart-detail-summary-label">Discount</span>
                                <span className="cart-detail-summary-amount">−{formatOrderMoney(discount)}</span>
                              </div>
                            )}
                            {Number(shipping) > 0 && (
                              <div className="cart-detail-summary-row">
                                <span className="cart-detail-summary-label">Shipping</span>
                                <span className="cart-detail-summary-amount">{formatOrderMoney(shipping)}</span>
                              </div>
                            )}
                            {Number(tax) > 0 && (
                              <div className="cart-detail-summary-row">
                                <span className="cart-detail-summary-label">Tax</span>
                                <span className="cart-detail-summary-amount">{formatOrderMoney(tax)}</span>
                              </div>
                            )}
                            <div className="cart-detail-summary-row">
                              <span className="cart-detail-summary-label">Grand Total</span>
                              <span className="cart-detail-summary-amount">{formatOrderMoney(grandTotal)}</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default OrderDetailPage;
